// AcademicAssistant.js
import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import "./AcademicAssistant.css";
import AgniTutorMathRenderer from './AgniTutorMathRenderer';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css'; 
import WelcomePanel from "./WelcomePanel";
import FeedbackModal from "./components/FeedbackModal";
// import DraggableScrollButton from "./DraggableScrollButton";

import MobileHistoryPanel from "./components/MobileHistoryPanel";
import SidebarHistoryPanel from "./components/SidebarHistoryPanel";
import AppHeader from "./components/AppHeader";

import { auth, signOut } from "./firebase"; // Adjust the path to your firebase.js configuration file
import { onAuthStateChanged } from "firebase/auth";
import GoogleLoginButton from "./components/GoogleAuthButton";



import "./fonts.css"; 
import "@fortawesome/fontawesome-free/css/all.min.css";

/* ---------------- Configuration ---------------- */
// NOTE: API key removed from frontend for security/cost reasons

const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5001";
 

/* ---------------- Storage keys ---------------- */
const SESSIONS_KEY = "aa_sessions_v1";
const COUNTS_KEY = "aa_like_counts_v1";
const HISTORY_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/* ---------------- Helpers ---------------- */
function removeAutoSymbols(str = "", forSolver = false) {
  if (!str) return str;

  try {
    let out = String(str);

    /* ==================================================
       🔥 IMPORTANT RULE
       - Display mode (forSolver = false):
         → DO NOT TOUCH LaTeX at all
       - Solver mode (forSolver = true):
         → Normalize symbols for calculation only
    ================================================== */

    if (!forSolver) {
      // 🔒 DO NOT touch LaTeX blocks
      if (out.includes("\\frac") || out.includes("$")) {
        return out;
      }

      return out
        .replace(/[\uFE00-\uFEFF\u200B-\u200F\u202A-\u202E]/g, "")
        .replace(/\r\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    }

    /* ==================================================
       ✅ SOLVER-ONLY NORMALIZATION
       (SAFE to convert math symbols here)
    ================================================== */

    out = out
      // powers
      .replace(/²/g, "^2")
      .replace(/³/g, "^3")
      .replace(/⁴/g, "^4")

      // operators
      .replace(/−/g, "-")
      .replace(/×/g, "*")
      .replace(/÷/g, "/")

      // arrows
      .replace(/\\rightarrow/g, "->")
      .replace(/→/g, "->");

    // --- Subscripts → normal digits (solver only) ---
    const subMap = {
      "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4",
      "₅": "5", "₆": "6", "₇": "7", "₈": "8", "₉": "9"
    };
    out = out.replace(/[\u2080-\u2089]/g, m => subMap[m] ?? m);

    // --- Cleanup invisible chars ---
    out = out.replace(/[\uFE00-\uFEFF\u200B-\u200F\u202A-\u202E]/g, "");

    // --- Normalize spacing ---
    out = out
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();

    return out;

  } catch (e) {
    console.warn("removeAutoSymbols failed", e);
    return String(str);
  }
}

function sanitizeMessageInSession(msg) {
  try {
    if (!msg || typeof msg !== "object") return msg;
    const cloned = { ...msg };
    if (typeof cloned.text === "string") cloned.text = removeAutoSymbols(cloned.text);
    return cloned;
  } catch (e) {
    return msg;
  }
}

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function saveJSON(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {}
}
function loadSessions() {
  try {
    const all = loadJSON(SESSIONS_KEY, []);
    const now = Date.now();
    const fresh = all
      .filter((s) => now - s.createdAt <= HISTORY_RETENTION_MS)
      .map((s) => {
        try {
          const cloned = { ...s };
          if (Array.isArray(cloned.messages)) cloned.messages = cloned.messages.map((m) => sanitizeMessageInSession(m));
          return cloned;
        } catch {
          return s;
        }
      });
    if (fresh.length !== all.length) saveJSON(SESSIONS_KEY, fresh);
    return fresh;
  } catch {
    return [];
  }
}
function saveSessions(sessions) {
  try {
    const sanitized = sessions.map((s) => {
      try {
        const c = { ...s };
        if (Array.isArray(c.messages)) c.messages = c.messages.map((m) => sanitizeMessageInSession(m));
        return c;
      } catch {
        return s;
      }
    });
    saveJSON(SESSIONS_KEY, sanitized);
  } catch {}
}
function loadCounts() {
  return loadJSON(COUNTS_KEY, {});
}
function saveCounts(obj) {
  saveJSON(COUNTS_KEY, obj);
}
function makeSession() {
  return {
    id: `${Date.now()}-${Math.floor(Math.random() * 10000)}`,
    createdAt: Date.now(),
    messages: [],
  };
}

// Add this helper near your other storage helpers
function groupSessionsByDate(sessions) {
  const groups = {
    Today: [],
    Yesterday: [],
    "Last 7 Days": [],
    Older: []
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = today - 86400000;
  const lastWeek = today - (7 * 86400000);

  sessions.forEach(s => {
    const ts = s.createdAt;
    if (ts >= today) groups.Today.push(s);
    else if (ts >= yesterday) groups.Yesterday.push(s);
    else if (ts >= lastWeek) groups["Last 7 Days"].push(s);
    else groups.Older.push(s);
  });

  return groups;
}

function resizeImage(file, maxSize = 768, quality = 0.85) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      return resolve(file); // non-image fallback
    }

    const img = new Image();
    const canvas = document.createElement("canvas");
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        let { width, height } = img;

        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject("Image resize failed");
            resolve(blob);
          },
          "image/jpeg",
          quality
        );
      };
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * getAcademicAnswer - safe image handling WITHOUT hosting
 * Handles cached + non-cached backend responses safely
 */
async function getAcademicAnswer(textPrompt, mediaFiles = [], opts = {}) {
  try {
    const cleanPrompt = (textPrompt || "").trim();

    // ===============================
    // EMPTY INPUT GUARD
    // ===============================
    if (!cleanPrompt && (!mediaFiles || mediaFiles.length === 0)) {
      return "⚠️ Please enter a question or upload an image.";
    }

    /* =====================================================
       ✅ LOCAL MATH SHORTCUT (TEXT ONLY)
    ===================================================== */
    if (/^[0-9\s()+\-*/%.]+$/.test(cleanPrompt)) {
      try {
        return `Answer: ${Function(
          `"use strict";return (${cleanPrompt})`
        )()}`;
      } catch {
        // fall through safely
      }
    }

    /* =====================================================
       ✅ IMAGE DETECTION
    ===================================================== */
    const hasImage = Array.isArray(mediaFiles) && mediaFiles.length > 0;

    /* =====================================================
       ✅ PROMPT
    ===================================================== */
    const prompt = hasImage
      ? `The image contains multiple math questions.

TASK:
1. Extract ALL questions from the image.
2. Solve EACH question one by one.
3. Show STEP-BY-STEP working.
4. Number the answers clearly.
5. Do NOT skip any question.
6. Do NOT give only a single number.

FORMAT YOUR RESPONSE EXACTLY LIKE THIS:

Example 1:
Question:
Steps:
Final Answer:

Example 2:
Question:
Steps:
Final Answer:

User request: ${cleanPrompt}`
      : cleanPrompt;

    /* =====================================================
       ✅ FORM DATA
    ===================================================== */
    const formData = new FormData();
    formData.append("prompt", prompt);

    if (Array.isArray(opts.history)) {
      formData.append("history", JSON.stringify(opts.history));
    }

    /* =====================================================
       ✅ IMAGE OPTIMIZATION
    ===================================================== */
    if (mediaFiles?.[0] instanceof File) {
      const optimizedImage = await resizeImage(mediaFiles[0]);
      formData.append("image", optimizedImage, "image.jpg");
    }

    /* =====================================================
       ✅ API CALL
    ===================================================== */
    const resp = await fetch(`${API_BASE}/api/gemini/multimodal`, {
      method: "POST",
      body: formData
    });

    if (!resp.ok) {
      const errText = await resp.text();
      console.error("Gemini backend error:", errText);
      return "⚠️ Unable to get response right now. Please try again.";
    }

    const json = await resp.json();

    /* =====================================================
       ✅ SAFE ANSWER EXTRACTION (CACHE + NON-CACHE)
    ===================================================== */
    let answer =
      json?.answer ??
      json?.candidates?.[0]?.content?.parts
        ?.map((p) => p.text)
        .filter(Boolean)
        .join(" ") ??
      json?.text ??
      json?.output_text ??
      json?.response ??
      "";

    // 🔒 CRITICAL SAFETY: never allow object rendering
    if (typeof answer !== "string") {
      try {
        answer = JSON.stringify(answer);
      } catch {
        answer = String(answer);
      }
    }

    answer = answer.trim();

    if (!answer) {
      return "⚠️ No answer received. Please try again.";
    }

    return answer;

  } catch (err) {
    console.error("getAcademicAnswer failed:", err);
    return "⚠️ Temporary issue occurred. Please retry your question.";
  }
}

// 1. IMPROVED: Subject Icon Detection for Premium Feel
function getSubjectIcon(text = "") {
  const p = text.toLowerCase();
  if (/(physics|chemistry|biology|science|reaction|mole|acid|base|salt|cell)/.test(p)) return "🧪";
  if (/(math|algebra|geometry|trigonometry|calculus|equation|calculate|solve)/.test(p)) return "📐";
  if (/(question paper|model paper|exam paper|test paper|marks|section)/.test(p)) return "📝";
  if (/(history|geography|civics|economics|political|social)/.test(p)) return "🌍";
  if (/(python|javascript|code|program|html|css|sql)/.test(p)) return "💻";
  return "✨"; 
}

const renderFormattedText = (text) => {
  if (!text) return null;

const normalizeContent = (input) => {
  let out = String(input || "");

  // =========================
  // LATEX CLEANUP
  // =========================

  out = out
    .replace(/\\\[/g, "$$")
    .replace(/\\\]/g, "$$")
    .replace(/\\\(/g, "$")
    .replace(/\\\)/g, "$");

  out = out.replace(/\\boxed\{([^}]*)\}/g, "$1");
  out = out.replace(/\\left/g, "");
  out = out.replace(/\\right/g, "");

  out = out.replace(
    /\\(?:text|mathrm|mathbf|mathit)\{([^}]*)\}/g,
    "$1"
  );

  // =========================
  // SYMBOL CLEANUP
  // =========================

  out = out.replace(/→/g, " → ");
  out = out.replace(/←/g, " ← ");
  out = out.replace(/⇒/g, " ⇒ ");
  out = out.replace(/⇔/g, " ⇔ ");
  out = out.replace(/≤/g, " ≤ ");
  out = out.replace(/≥/g, " ≥ ");
  out = out.replace(/≠/g, " ≠ ");
  out = out.replace(/±/g, " ± ");
  out = out.replace(/∞/g, " ∞ ");

  // =========================
  // MATH FIXES
  // =========================

  out = out.replace(/\bdsdt\b/g, "ds/dt");
  out = out.replace(/\bdvdt\b/g, "dv/dt");
  out = out.replace(/\bddt\b/g, "d/dt");
  out = out.replace(/\bdydx\b/g, "dy/dx");

  out = out.replace(/\bx\^?2\b/g, "x²");
  out = out.replace(/\bx\^?3\b/g, "x³");

  out = out.replace(/\bt\^?2\b/g, "t²");
  out = out.replace(/\bt\^?3\b/g, "t³");

  // x - 3 = 0 => x = 3

  out = out.replace(
    /(x\s*[-+]\s*\d+\s*=\s*0)\s*=>\s*(x\s*=\s*[-+]?\d+(?:\.\d+)?)/gi,
    "$1\n\n⇒ **$2**"
  );

  out = out.replace(
    /^\s*=>\s*(.+)$/gim,
    "⇒ **$1**"
  );

  out = out.replace(
    /^x\s*=\s*([^\n]+)$/gim,
    "👉 **x = $1**"
  );

  out = out.replace(
    /^y\s*=\s*([^\n]+)$/gim,
    "👉 **y = $1**"
  );

  out = out.replace(
    /^Therefore[:,]?\s*(.+)$/gim,
    "∴ **$1**"
  );

  out = out.replace(
    /^Hence[:,]?\s*(.+)$/gim,
    "∴ **$1**"
  );

  // =========================
// CHEMISTRY
// =========================

// Common formulas
const chemistryMap = {
  H2O: "H₂O",
  CO2: "CO₂",
  O2: "O₂",
  N2: "N₂",
  H2: "H₂",
  SO2: "SO₂",
  SO3: "SO₃",
  NH3: "NH₃",
  CH4: "CH₄",
  H2SO4: "H₂SO₄",
  HNO3: "HNO₃",
  Na2CO3: "Na₂CO₃",
  CaCO3: "CaCO₃",
  Fe2O3: "Fe₂O₃",
  Al2O3: "Al₂O₃",
  CuSO4: "CuSO₄",
  NaOH: "NaOH",
  HCl: "HCl",
  H2O2: "H₂O₂",
};

Object.entries(chemistryMap).forEach(([plain, pretty]) => {
  const normal = new RegExp(`\\b${plain}\\b`, "g");
  const underscore = new RegExp(
    plain.replace(/(\d)/g, "_$1"),
    "g"
  );

  out = out.replace(normal, pretty);
  out = out.replace(underscore, pretty);
});

// Chemistry arrows
out = out.replace(/\barrow\b/gi, "→");
out = out.replace(/-->/g, "→");
out = out.replace(/->/g, "→");
out = out.replace(/=>/g, "→");

// Balance heading
out = out.replace(
  /^Balance\s*:?\s*(.+)$/gim,
  (_, eq) =>
    `## ⚗️ Balance Equation\n\n**${eq.trim()}**`
);

// Verification heading
out = out.replace(
  /^Verification\s*:?\s*$/gim,
  "## ✅ Verification"
);

// Final balanced equation
out = out.replace(
  /^The balanced chemical equation is\s*:?\s*\n?([^\n]+)/gim,
  (_, eq) =>
    `\n## 🎯 FINAL ANSWER\n\n### **${eq.trim()}**\n`
);

  // =========================
  // PHYSICS UNITS
  // =========================

  out = out.replace(/\bm\/s\^?2\b/g, "m/s²");
  out = out.replace(/\bcm\^?2\b/g, "cm²");
  out = out.replace(/\bcm\^?3\b/g, "cm³");
  out = out.replace(/\bkm\^?2\b/g, "km²");
  out = out.replace(/\bkm\^?3\b/g, "km³");
  out = out.replace(/\bm\^?2\b/g, "m²");
  out = out.replace(/\bm\^?3\b/g, "m³");
  out = out.replace(/\bkg\/m3\b/g, "kg/m³");

  out = out.replace(
    /(\d)(m\/s²|m\/s|cm|mm|km|kg|g|N|J|W)/g,
    "$1 $2"
  );

  // =========================
  // REMOVE INLINE CODE
  // =========================

  out = out.replace(/`([^`\n]+)`/g, "$1");

  // =========================
  // SECTION HEADERS
  // =========================

  out = out.replace(/^Question:?/gim, "## ❓ Question");
  out = out.replace(/^Given:?/gim, "## 📘 Given");
  out = out.replace(/^Formula:?/gim, "## 📐 Formula");

  out = out.replace(
    /^Substitution\s*\/?\s*Reaction:?/gim,
    "## 🔄 Substitution"
  );

  out = out.replace(/^Calculation:?/gim, "## 🧮 Calculation");
  out = out.replace(/^Explanation:?/gim, "## 💡 Explanation");
  out = out.replace(/^Conclusion:?/gim, "## ✅ Conclusion");

  // =========================
// FINAL ANSWER
// =========================

out = out.replace(
  /\*\*Final Answer:?\*\*\s*([^\n]+)/gi,
  (_, answer) =>
    `\n## 🎯 FINAL ANSWER\n\n### **${answer.trim()}**\n`
);

out = out.replace(
  /^Final Answer\s*:?\s*\n?([^\n]+)/gim,
  (_, answer) =>
    `\n## 🎯 FINAL ANSWER\n\n### **${answer.trim()}**\n`
);

out = out.replace(
  /^FINAL ANSWER\s*:?\s*\n?([^\n]+)/gim,
  (_, answer) =>
    `\n## 🎯 FINAL ANSWER\n\n### **${answer.trim()}**\n`
);
  // =========================
  // COMMON SYMBOLS
  // =========================

  out = out.replace(/\btheta\b/g, "\\theta");
  out = out.replace(/\balpha\b/g, "\\alpha");
  out = out.replace(/\bbeta\b/g, "\\beta");
  out = out.replace(/\bgamma\b/g, "\\gamma");
  out = out.replace(/\bpi\b/g, "\\pi");

  out = out.replace(/lim\s*x\s*->\s*0/gi, "lim(x → 0)");
  out = out.replace(/lim\s*x\s*→\s*0/gi, "lim(x → 0)");

  // Remove markdown table alignment rows
out = out.replace(/^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/gm, "");

// Convert table pipes to readable separators
out = out.replace(/\|/g, " ");

  // =========================
  // AUTO LATEX WRAP
  // =========================

  const latexPattern =
    /\\(frac|sqrt|sum|int|lim|sin|cos|tan|alpha|beta|gamma|theta|pi)/;

  out = out
    .split("\n")
    .map((line) => {
      const trimmed = line.trim();

      if (
        trimmed &&
        latexPattern.test(trimmed) &&
        !trimmed.includes("$") &&
        !trimmed.startsWith("#")
      ) {
        return `\n$$${trimmed}$$\n`;
      }

      return line;
    })
    .join("\n");

  // =========================
  // CLEANUP
  // =========================

  // Remove standalone bullet/star lines
  out = out.replace(/^\s*\*\s*$/gm, "");

  // Remove multiple consecutive stars
  out = out.replace(/^\s*\*{2,}\s*$/gm, "");

  // Remove empty markdown bullets
  out = out.replace(/^\s*[-*]\s*$/gm, "");

  out = out.replace(/^\s*\*\s*$/gm, "\n---\n");
  out = out.replace(/\*{3,}/g, "**");
  out = out.replace(/_{4,}/g, "");
  out = out.replace(/----+/g, "---");

  out = out.replace(/[ \t]{2,}/g, " ");
  out = out.replace(/\n{3,}/g, "\n\n");

  return out.trim();
};



  const processedText = normalizeContent(text)
  .replace(
    /(https?:\/\/[^\s<]+)/g,
    (url) => `[${url}](${url})`
  );

  return (
  <div className="aa-ai-body premium-paper-basis">
    <ReactMarkdown
      remarkPlugins={[remarkMath]}
      rehypePlugins={[
        rehypeRaw,
        [rehypeKatex, { strict: false }]
      ]}
      components={{
        h1: ({ children }) => (
          <h1 className="paper-main-header">{children}</h1>
        ),

        h2: ({ children }) => (
          <h2 className="paper-section-header">{children}</h2>
        ),

        h3: ({ children }) => (
          <h3 className="paper-sub-header">{children}</h3>
        ),

        p: ({ children }) => (
          <div className="premium-p-text">
            {children}
          </div>
        ),

        ol: ({ children }) => (
          <ol className="premium-official-list">
            {children}
          </ol>
        ),

        ul: ({ children }) => (
          <ul className="premium-official-list">
            {children}
          </ul>
        ),

        li: ({ children }) => (
          <li className="premium-official-li">
            {children}
          </li>
        ),

        hr: () => <div className="paper-separator" />,

        // ✅ OPEN LINKS IN NEW TAB
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="ai-answer-link"
          >
            {children}
          </a>
        ),

        code({ inline, children }) {
          if (inline) {
            return (
              <code className="inline-code">
                {children}
              </code>
            );
          }

          return (
            <pre className="code-block">
              <code>{children}</code>
            </pre>
          );
        }
      }}
    >
      {processedText}
    </ReactMarkdown>
  </div>
);
};

export default function AcademicAssistantUI() {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showCopyToast, setShowCopyToast] = useState(false);

  useEffect(() => {
    if (!showCopyToast) return undefined;
    const t = setTimeout(() => setShowCopyToast(false), 1100);
    return () => clearTimeout(t);
  }, [showCopyToast]);

  const [input, setInput] = useState("");
  const [files, setFiles] = useState([]); // actual File objects to send
  const [previews, setPreviews] = useState([]); // preview objects for UI
  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [playingMsgId, setPlayingMsgId] = useState(null);
  
  const [uiMode, setUiMode] = useState("welcome"); 
  const [confirmingDelete, setConfirmingDelete] = useState(null); // Stores the session ID

  const [showSplash, setShowSplash] = useState(true);
  const [showHistoryMobile, setShowHistoryMobile] = useState(false);

  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false); 

  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const composerAnchorRef = useRef(null);
  const aaCardRef = useRef(null);
  const selectedVoiceRef = useRef(null);
  const prevMessagesLenRef = useRef(0);
  const [leftFooterStyle, setLeftFooterStyle] = useState({});
  const ttsSessionIdRef = useRef(0);

  const isTtsPausedRef = useRef(false);   // tracks whether pause was intentional
  const lastPlayedMsgRef = useRef(null);  // remembers which msgId is active
 
  const [pendingMath, setPendingMath] = useState(null);
 
  const [showScrollDown, setShowScrollDown] = useState(false);

  const [showSocialMenu, setShowSocialMenu] = useState(false);
  const socialMenuRef = useRef(null);

  const [selectedVote, setSelectedVote] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState("general");
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE;
  


  //Firebase Login

  const [currentUser, setCurrentUser] = useState(null);

  // Listen for Firebase Auth state changes globally
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

// Monitor scroll position to toggle floating scroll-down button
useEffect(() => {
  const container = scrollRef.current;
  if (!container) return;

  let animationFrameId = null;
  let ticking = false;

  const updateScrollState = () => {
    // Math.max prevents negative values on mobile elastic/bounce scrolling
    const distanceFromBottom = Math.max(
      0,
      container.scrollHeight - container.scrollTop - container.clientHeight
    );

    // Show button if user has scrolled up more than 150px from bottom
    setShowScrollDown(distanceFromBottom > 150);
    ticking = false;
  };

  const handleScroll = () => {
    if (!ticking) {
      animationFrameId = window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  // 1. Immediate check
  handleScroll();

  // 2. Delayed check to handle mobile rendering/font/layout reflow delays on page load
  const loadTimer = setTimeout(updateScrollState, 200);

  // 3. Monitor container size changes directly (essential for mobile address bar toggles)
  const resizeObserver = new ResizeObserver(() => {
    handleScroll();
  });
  resizeObserver.observe(container);

  container.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleScroll, { passive: true });

  return () => {
    clearTimeout(loadTimer);
    resizeObserver.disconnect();
    container.removeEventListener("scroll", handleScroll);
    window.removeEventListener("resize", handleScroll);
    if (animationFrameId) {
      window.cancelAnimationFrame(animationFrameId);
    }
  };
}, [messages]);

 const handleSubmitFeedback = async () => {
  try {
    const response = await fetch(
      `${API_BASE}/api/feedback`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          category: feedbackType,
          feedback: feedbackText
        })
      }
    );

    if (!response.ok) {
      throw new Error("Feedback submission failed");
    }

    setShowFeedbackSuccess(true);

    setTimeout(() => {
      setShowFeedbackSuccess(false);
    }, 3000);

    setFeedbackText("");
    setFeedbackType("");
    setShowFeedback(false);

  } catch (err) {
    console.error("Feedback Error:", err);
  }
};

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        socialMenuRef.current &&
        !socialMenuRef.current.contains(e.target)
      ) {
        setShowSocialMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const loadedSessions = loadSessions();
    const loadedCounts = loadCounts();
    setCounts(loadedCounts || {});

    // 🔑 read last selected session
    const lastSessionId = localStorage.getItem("agnitutor:lastSessionId");

    if (loadedSessions.length > 0) {
      setSessions(loadedSessions);

      // ✅ restore last selected chat if exists
      const lastSession =
        lastSessionId &&
        loadedSessions.find((s) => s.id === lastSessionId);

      const activeSession = lastSession || loadedSessions[0];

      setCurrentSessionId(activeSession.id);
      setMessages(activeSession.messages);
      prevMessagesLenRef.current = activeSession.messages.length;
    } else {
      // ✅ no sessions → create new one
      const s = makeSession();
      setSessions([s]);
      setCurrentSessionId(s.id);
      setMessages(s.messages);
      prevMessagesLenRef.current = s.messages.length;
      saveSessions([s]);

      // clear last session reference
      localStorage.removeItem("agnitutor:lastSessionId");
    }

    function pickVoice() {
      const voices = window.speechSynthesis.getVoices();
      const v = selectFemaleVoice(voices);
      if (v) selectedVoiceRef.current = v;
    }

    pickVoice();
    window.speechSynthesis.onvoiceschanged = pickVoice;

    const t = setTimeout(() => setShowSplash(false), 1200);
    return () => {
      clearTimeout(t);
      window.speechSynthesis.onvoiceschanged = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (messages && messages.length > 0) {
      setUiMode("chat");
    }
  }, [messages]);

  // Persist helpers (unchanged)
  function persistSessions(updated) {
    setSessions(updated);
    saveSessions(updated);
  }
  function persistCountsState(obj) {
    setCounts(obj);
    saveCounts(obj);
  }
  function persistCurrentMessages(updatedMessages, sessionId = currentSessionId) {
    const updated = sessions.map((s) => (s.id === sessionId ? { ...s, messages: updatedMessages } : s));
    persistSessions(updated);
    if (sessionId === currentSessionId) setMessages(updatedMessages);
  }

  function startChatWithMode(modeLabel) {
    setUiMode("chat");
    setInput(modeLabel + ": ");
  }

  function appendToSession(msg, sessionId = currentSessionId) {
    setSessions((prevSessions) => {
      const updated = prevSessions.map((s) =>
        s.id === sessionId
          ? { ...s, messages: [...s.messages, msg] }
          : s
      );

      saveSessions(updated);

      // sync chat UI immediately
      if (sessionId === currentSessionId) {
        const current = updated.find(s => s.id === sessionId);
        if (current) setMessages(current.messages);
      }

      return updated;
    });
  }

 function newSessions() {
  const s = makeSession();

  const updated = [s, ...sessions];

  persistSessions(updated);

  setCurrentSessionId(s.id);
  setMessages([]);

  setInput("");
  setFiles([]);
  setPreviews([]);

  setUiMode("welcome"); // ✅ important
}
  
  function openSession(id) {
    const s = sessions.find((x) => x.id === id);
    if (!s) return;

    // ✅ remember selected session
    localStorage.getItem("agnitutor:lastSessionId");

    setCurrentSessionId(id);
    setMessages(s.messages);

    // reset composer state (keep this as you had)
    setInput("");
    setFiles([]);
    setPreviews([]);

    // close mobile history
    setShowHistoryMobile(false);
  }

  function deleteSession(id) {
    const updated = sessions.filter((s) => s.id !== id);
    persistSessions(updated);
    if (id === currentSessionId) {
      if (updated.length > 0) {
        const last = updated[updated.length - 1];
        setCurrentSessionId(last.id);
        setMessages(last.messages);
      } else {
        const s = makeSession();
        persistSessions([s]);
        setCurrentSessionId(s.id);
        setMessages(s.messages);
      }
    }
  }

  function openDeleteAllModal() { setShowDeleteAllModal(true); }
  function closeDeleteAllModal() { setShowDeleteAllModal(false); }

  function confirmDeleteAll() {
    try {
      const s = makeSession();
      persistSessions([s]);
      setCurrentSessionId(s.id);
      setMessages(s.messages);
      persistCountsState({});
    } catch (e) {
      console.warn("confirmDeleteAll failed", e);
    } finally {
      setShowDeleteAllModal(false);
    }
  }

  /* Danger confirmation modal (centered, accessible) */
  function DangerModal({ open, title = "Confirm", description = "", confirmLabel = "Delete", cancelLabel = "Cancel", onConfirm, onCancel }) {
    const css = `
      .aa-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(2,6,23,0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 14000;
        padding: 20px;
      }
      .aa-modal {
        width: 100%;
        max-width: 520px;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 18px 50px rgba(2,6,23,0.2);
        padding: 20px;
        color: #0f172a;
        font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial;
      }
      .aa-modal h3 { margin: 0 0 6px; font-size: 18px; }
      .aa-modal p { margin: 6px 0 18px; color: #374151; line-height: 2.2; }
      .aa-modal .footer { display:flex; justify-content:flex-end; gap:10px; margin-top:8px; }
      .aa-modal .btn { padding:8px 12px; border-radius:8px; border:1px solid transparent; cursor:pointer; font-size:14px; }
      .aa-modal .btn.cancel { background: transparent; border-color: #e6e6e6; color:#374151; }
      .aa-modal .btn.danger { background: #dc2626; color:#fff; border-color: #dc2626; box-shadow: 0 8px 20px rgba(220,38,38,0.12); }
    `;
    if (!open) return null;
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        <div className="aa-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="aa-modal-title">
          <div className="aa-modal">
            <h3 id="aa-modal-title">{title}</h3>
            <p>{description}</p>
            <div className="footer">
              <button className="btn cancel" onClick={onCancel}>{cancelLabel}</button>
              <button className="btn danger" onClick={onConfirm}>{confirmLabel}</button>
            </div>
          </div>
        </div>
      </>
    );
  }

// Voice selection helper used by TTS
function selectFemaleVoice(voices) {
  if (!voices || voices.length === 0) return null;

  const preferredVoices = [
    "Google UK English Female",
    "Google US English Female",
    "Samantha",
    "Victoria",
    "Joanna",
    "Kendra",
    "Ivy"
  ];

  // exact match first
  for (const name of preferredVoices) {
    const v = voices.find((vc) => vc && vc.name === name);
    if (v) return v;
  }

  // look for 'female' in name
  const femaleByKeyword = voices.find((vc) => vc && typeof vc.name === "string" && vc.name.toLowerCase().includes("female"));
  if (femaleByKeyword) return femaleByKeyword;

  // fallback to first English voice
  const en = voices.find((vc) => vc && typeof vc.lang === "string" && vc.lang.startsWith("en"));
  if (en) return en;

  // final fallback
  return voices[0] || null;
}

// Safe image URL sanitizer for thumbnails/full images
function sanitizeImageUrl(url, prompt = "") {
  try {
    if (!url) {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='#111827'>No image</text></svg>`;
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }
    const s = String(url).trim();
    if (s.startsWith("data:")) return s;
    if (s.startsWith("//")) return window.location.protocol + s;
    if (/^[\w.-]+\//.test(s)) return "https://" + s;
    if (s.startsWith("http://") || s.startsWith("https://")) return s;
    return s;
  } catch (e) {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><rect width='100%' height='100%' fill='#f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='18' fill='#111827'>No image</text></svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }
}

async function handleCopyText(text, setShowCopyToast) {
  try {
    if (!text) return false;

    // 🏢 UPDATE YOUR COMPANY DETAILS HERE ONCE
    const COMPANY_NAME = "AGNI Tutor";
    const COMPANY_LINK = "https://agnitutor.com";

    // 1. CLEANING & MATH FORMATTING PHASE
    let cleaned = String(text)
      .replace(/\[GeneratedImageURL:[^\]]*\]/g, "") // Remove AI image tags
      .replace(/<Diagram:[^>]*>/gi, "")             // Remove diagram tags
      .replace(/Caption:\s*.*$/gim, "")             // Remove captions
      .replace(/<\/?[^>]+(>|$)/g, "");              // Remove HTML tags

      // Convert LaTeX Matrices to Text Grid format for clean clipboard pasting
      cleaned = cleaned.replace(/\\begin\{p?matrix\}([\s\S]*?)\\end\{p?matrix\}/g, (match, inner) => {
        let rows = inner.split(/\\\\/).map(row => 
          "[" + row.split("&").map(cell => cell.trim()).join("   ") + "]"
        );
        return "\n" + rows.join("\n") + "\n";
      });

      // Convert fractions (supports multiple/nested layers)
      for (let i = 0; i < 3; i++) {
        cleaned = cleaned.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)");
      }

      // Convert square roots and common math operators
      cleaned = cleaned
        .replace(/\\sqrt\{([^}]*)\}/g, "√($1)")
        .replace(/\\pm/g, "±")
        .replace(/\\times/g, "×")
        .replace(/\\cdot/g, "·")
        .replace(/\\pi/g, "π")
        .replace(/\\theta/g, "θ")
        .replace(/\\alpha/g, "α")
        .replace(/\\beta/g, "β")
        .replace(/\\infty/g, "∞");

      // Clean syntax wrappers and layout commands
      cleaned = cleaned
        .replace(/\\(?:boxed|box)\{([^}]+)\}/g, "$1")
        .replace(/\\left\(|\\right\)/g, (m) => m === '\\left\(' ? '(' : ')')
        .replace(/\\left\[|\\right\]/g, (m) => m === '\\left\[' ? '[' : ']')
        .replace(/\\(?:text|www|mathrm|mathbf|mathit)\{([^}]+)\}/g, "$1")
        
        // Remove LaTeX math block symbols ($ and $$)
        .replace(/\$\$(.*?)\$\$/gs, '$1')
        .replace(/\$(.*?)\$/gs, '$1')

        // Clean formatting elements
        .replace(/_\{([^}]*)\}/g, '_$1')             // Subscripts
        .replace(/\^\{([^}]*)\}/g, '^$1')             // Superscripts
        .replace(/\*\*/g, '')                         // Remove markdown bold stars
        .replace(/^#{1,6}\s*/gm, '')                  // Remove markdown headings
        .replace(/\n{3,}/g, '\n\n')                   // Collapse excessive blank lines
        .trim();

    if (!cleaned) return false;

    // Append clean company footer to the copied text
    cleaned += `\n\nSolved using ${COMPANY_NAME}\n${COMPANY_LINK}`;

    // 2. COPY TO CLIPBOARD
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(cleaned);
    } else {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = cleaned;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (!ok) throw new Error("copy failed");
    }

    // 3. Show success toast
    if (typeof setShowCopyToast === "function") setShowCopyToast(true);
    return true;

  } catch (err) {
    console.warn("handleCopyText failed", err);
    if (typeof setShowCopyToast === "function") setShowCopyToast(false);
    return false;
  }
}

// speakText (robust + abortable)
async function speakText(text, msgId) {
  if (!("speechSynthesis" in window)) {
    alert("Text-to-speech not supported in this browser.");
    return;
  }

  // If there's a paused speech session, resume it instead of restarting.
  try {
    if (window.speechSynthesis && window.speechSynthesis.paused) {
      try {
        window.speechSynthesis.resume();
        setPlayingMsgId(msgId);
      } catch (e) {
        console.warn("speakText: resume failed", e);
      }
      return;
    }
  } catch (e) {
    console.warn("speakText: paused check failed", e);
  }

  // create a new local session id for this speak call
  const mySession = (ttsSessionIdRef.current || 0) + 1;
  ttsSessionIdRef.current = mySession;

  try {
    const cleanText = getReadableText(text);
    if (!cleanText) {
      console.info("speakText: nothing to speak after cleaning.");
      return;
    }

    const voices = await ensureVoicesLoaded(1200);
    if (voices && voices.length > 0 && !selectedVoiceRef.current) {
      selectedVoiceRef.current = selectFemaleVoice(voices);
    }

    // re-resolve selected voice to a current instance if possible
    if (selectedVoiceRef.current && voices && voices.length > 0) {
      const matched = voices.find((v) => v.name === selectedVoiceRef.current.name);
      if (matched) selectedVoiceRef.current = matched;
    }

    // Only cancel previous speech if something is actively speaking (not paused).
    try {
      if (window.speechSynthesis && window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        try { window.speechSynthesis.cancel(); } catch (e) { console.warn("TTS cancel prior", e); }
      }
    } catch (e) {
      console.warn("speakText: cancel check failed", e);
    }

    // chunk logic (same as before)
    const MAX_LEN = 2400;
    const toSpeakParts = [];
    if (cleanText.length > MAX_LEN) {
      const sentences = cleanText.match(/[^\.!\?]+[\.!\?]*/g) || [cleanText];
      let current = "";
      sentences.forEach((s) => {
        if ((current + s).length > MAX_LEN) {
          if (current) { toSpeakParts.push(current.trim()); current = s; }
          else { toSpeakParts.push(s.trim()); current = ""; }
        } else current += s;
      });
      if (current) toSpeakParts.push(current.trim());
    } else {
      toSpeakParts.push(cleanText);
    }

    console.info("speakText: speaking", { msgId, parts: toSpeakParts.length, voice: selectedVoiceRef.current?.name });
    setPlayingMsgId(msgId);

    // Adjust these rates to taste (lower = slower)
    const DEFAULT_RATE = 0.80;   // default speaking rate (slower)
    const LANG_INDIC_RATE = 0.82; // slightly different rate for Indic languages

    for (const part of toSpeakParts) {
      // check whether we've been asked to stop since this loop started
      if (ttsSessionIdRef.current !== mySession) {
        console.info("speakText: aborted before starting next part", { mySession, current: ttsSessionIdRef.current });
        break;
      }

      // wait for this utterance to finish or for abort
      const finished = await new Promise((resolvePart) => {
        let resolvedFlag = false;
        const u = new SpeechSynthesisUtterance(part);

        // --- UPDATED BLOCK: language detection and voice selection ---
        // detect common Indic script blocks; fall back to en-IN
        let detectedLang = null;
        try {
          if (/[\u0B80-\u0BFF]/.test(part)) {
            // Tamil Unicode block
            detectedLang = "ta-IN";
          } else if (/[\u0900-\u097F]/.test(part)) {
            // Devanagari (Hindi, Marathi, etc.)
            detectedLang = "hi-IN";
          } else if (/[\u0980-\u09FF]/.test(part)) {
            // Bengali
            detectedLang = "bn-IN";
          } else if (/[\u0C00-\u0C7F]/.test(part)) {
            // Telugu
            detectedLang = "te-IN";
          } else if (/[\u0C80-\u0CFF]/.test(part)) {
            // Kannada
            detectedLang = "kn-IN";
          } else if (/[\u0D00-\u0D7F]/.test(part)) {
            // Malayalam
            detectedLang = "ml-IN";
          } else {
            detectedLang = null;
          }
        } catch (e) {
          console.warn("speakText: lang detect failed", e);
          detectedLang = null;
        }

        // get available voices
        let curVoices = [];
        try {
          curVoices = window.speechSynthesis.getVoices() || [];
        } catch (e) {
          console.warn("speakText: getVoices() failed", e);
        }

        try {
          const nameMatch = selectedVoiceRef.current
            ? curVoices.find((v) => v.name === selectedVoiceRef.current.name)
            : null;

          const langExact = detectedLang ? curVoices.find((v) => v.lang === detectedLang) : null;
          const langStarts = detectedLang ? curVoices.find((v) => v.lang && v.lang.startsWith(detectedLang.split("-")[0])) : null;

          if (nameMatch) {
            u.voice = nameMatch;
          } else if (langExact) {
            u.voice = langExact;
          } else if (langStarts) {
            u.voice = langStarts;
          } else if (selectedVoiceRef.current) {
            const loose = curVoices.find((v) => v.name === selectedVoiceRef.current.name);
            if (loose) u.voice = loose;
            else if (curVoices.length) u.voice = curVoices[0];
          } else if (curVoices.length) {
            u.voice = curVoices[0];
          }
        } catch (e) {
          console.warn("speakText: voice selection failed", e);
        }

        // set utterance language: prefer detectedLang, else derive from voice, else en-IN
        u.lang = detectedLang || u.voice?.lang || "en-IN";

        // Set rate based on detected language (lower = slower)
        u.rate = detectedLang ? LANG_INDIC_RATE : DEFAULT_RATE;
        u.pitch = 1.05;
        u.volume = 1.0;
        // --- END UPDATED BLOCK ---

        // Safety poller for abort
        const pollInterval = 120;
        const poller = setInterval(() => {
          if (ttsSessionIdRef.current !== mySession) {
            try { window.speechSynthesis.cancel(); } catch (e) {}
            clearInterval(poller);
            if (!resolvedFlag) { resolvedFlag = true; resolvePart({ ok: false, reason: "aborted" }); }
          }
        }, pollInterval);

        u.onend = () => {
          try { clearInterval(poller); } catch (e) {}
          if (!resolvedFlag) { resolvedFlag = true; resolvePart({ ok: true, reason: "end" }); }
        };
        u.onerror = (ev) => {
          try { clearInterval(poller); } catch (e) {}
          if (!resolvedFlag) { resolvedFlag = true; console.warn("TTS part error", ev); resolvePart({ ok: false, reason: "error" }); }
        };

        try {
          window.speechSynthesis.speak(u);
        } catch (e) {
          try { clearInterval(poller); } catch (ee) {}
          if (!resolvedFlag) { resolvedFlag = true; console.warn("speakText: speak() threw", e); resolvePart({ ok: false, reason: "throw" }); }
        }
      });

      // if aborted, break
      if (ttsSessionIdRef.current !== mySession) {
        console.info("speakText: detected abort after part", { mySession, current: ttsSessionIdRef.current });
        break;
      }
    }
  } catch (err) {
    console.warn("speakText unexpected error", err);
  } finally {
    // If this session is still the active session, clear playing state
    if (ttsSessionIdRef.current === mySession) setPlayingMsgId(null);
  }
}

// Pause current speech (keeps the current queue so resume() will continue)
function pauseSpeaking() {
  try {
    if (!("speechSynthesis" in window)) return;
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      console.info("TTS paused");
      setPlayingMsgId(null);
    }
  } catch (e) { console.warn("pauseSpeaking failed", e); }
}

function resumeSpeaking(msgId) {
  try {
    if (!("speechSynthesis" in window)) return;
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      console.info("TTS resumed");
      setPlayingMsgId(msgId || playingMsgId);
    }
  } catch (e) { console.warn("resumeSpeaking failed", e); }
}

// hardStop true = cancel and forget position; hardStop false = pause (soft stop)
function stopSpeaking(hardStop = true) {
  try {
    if (!("speechSynthesis" in window)) return;
    const synth = window.speechSynthesis;
    if (hardStop) {
      console.log("stopSpeaking (hard) called — stack:", new Error().stack);
      ttsSessionIdRef.current = (ttsSessionIdRef.current || 0) + 1;
      try { synth.cancel(); } catch (e) { console.warn("stopSpeaking: cancel failed", e); }
      setPlayingMsgId(null);
      lastPlayedMsgRef.current = null;
      isTtsPausedRef.current = false;
      console.info("TTS stopped (hard)");
    } else {
      pauseSpeaking();
    }
  } catch (e) {
    console.warn("stopSpeaking unexpected", e);
  }
}

// Clean and extract speakable text from AI message text
function getReadableText(raw) {
  try {
    if (!raw) return "";
    let s = String(raw);

    // Remove bracketed generated image URLs: [GeneratedImageURL:...]
    s = s.replace(/\[GeneratedImageURL:[^\]]*\]/g, " ");

    // Remove explicit caption lines: "Caption: ..." (single-line)
    s = s.replace(/^\s*Caption:\s*.*$/gim, " ");

    // Remove the diagram tag line if present: <Diagram: ...>
    s = s.replace(/<Diagram:[^>]*>/gi, " ");

    // Remove markdown-style titles like "### Title" or "# Title"
    s = s.replace(/^\s{0,3}#{1,6}\s.*$/gim, " ");

    // Remove any leftover HTML tags (if any)
    s = s.replace(/<\/?[^>]+(>|$)/g, " ");

    // Collapse multi-newlines and trim
    s = s.replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n\n").trim();

    // Remove any auto symbols (global)
    s = typeof removeAutoSymbols === "function" ? removeAutoSymbols(s) : s;

    // If text is still very long, limit length for reliability
    if (s.length > 4000) s = s.slice(0, 4000) + " ...";

    return s;
  } catch (e) {
    console.warn("getReadableText failed", e);
    return typeof removeAutoSymbols === "function" ? removeAutoSymbols(String(raw || "")) : String(raw || "");
  }
}

// Wait for voices to populate (small timeout)
function ensureVoicesLoaded(timeoutMs = 1000) {
  return new Promise((resolve) => {
    try {
      const voicesNow = window.speechSynthesis.getVoices();
      if (voicesNow && voicesNow.length > 0) return resolve(voicesNow);
      let resolved = false;
      const handler = () => {
        if (resolved) return;
        const vs = window.speechSynthesis.getVoices();
        if (vs && vs.length > 0) {
          resolved = true;
          if (typeof window.speechSynthesis.removeEventListener === "function") {
            window.speechSynthesis.removeEventListener("voiceschanged", handler);
          } else {
            window.speechSynthesis.onvoiceschanged = null;
          }
          resolve(vs);
        }
      };
      if (typeof window.speechSynthesis.addEventListener === "function") {
        window.speechSynthesis.addEventListener("voiceschanged", handler);
      } else {
        window.speechSynthesis.onvoiceschanged = handler;
      }
      // fallback timeout
      setTimeout(() => {
        if (!resolved) {
          if (typeof window.speechSynthesis.removeEventListener === "function") {
            window.speechSynthesis.removeEventListener("voiceschanged", handler);
          } else {
            window.speechSynthesis.onvoiceschanged = null;
          }
          resolve(window.speechSynthesis.getVoices() || []);
        }
      }, timeoutMs);
    } catch (e) {
      resolve([]);
    }
  });
}

/* Tiny CopyToast component: call setShowCopyToast(true) to display for ~1.1s */
function CopyToast({ visible }) {
  const css = `
    .aa-copy-toast {
      position: fixed;
      right: 22px;
      bottom: 86px;
      background: rgba(17,24,39,0.95);
      color: #fff;
      padding: 8px 12px;
      border-radius: 12px;
      font-size: 13px;
      box-shadow: 0 6px 18px rgba(2,6,23,0.12);
      transform: translateY(8px);
      opacity: 0;
      pointer-events: none;
      transition: opacity 220ms ease, transform 220ms ease;
      z-index: 99999;
    }
    .aa-copy-toast.show { opacity: 1; transform: translateY(0); pointer-events: auto; }
  `;
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div role="status" aria-live="polite" className={`aa-copy-toast ${visible ? "show" : ""}`}>
        Copied to clipboard ✓
      </div>
    </>
  );
}

/* ---------------- Premium UI Formatter ---------------- */
const formatPremiumResponse = (text) => {
  if (!text) return "";

  let formatted = text;

  // 1. Transform "Step X:" into a styled badge
  formatted = formatted.replace(
    /(Step\s\d+:)/gi, 
    '<span className="step-badge">$1</span>'
  );

  // 2. Transform "Final Answer:" into a highlighted result box
  formatted = formatted.replace(
    /(Final\sAnswer:.*)/gi,
    '<div className="final-result-container">$1</div>'
  );

  // 3. Add a visual divider before major sections
  formatted = formatted.replace(
    /(###\s\*\*.*?\*\*)/g,
    '<div className="section-divider"></div>$1'
  );

  return formatted;
};

/* ---------- Scrolling & UI helpers ---------- */
function scrollAIMessageTopAboveComposer(msgEl) {
  if (!msgEl) return;
  const container = scrollRef.current;
  if (!container) return;
  const composerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--composer-h")) || 112;
  const SMALL_OFFSET = 12;
  try {
    const visibleHeight = container.clientHeight;
    const msgTopInContainer = msgEl.offsetTop;
    const targetY = Math.max(0, visibleHeight - composerH - SMALL_OFFSET);
    const desiredScrollTop = msgTopInContainer - targetY;
    const maxScrollTop = Math.max(0, container.scrollHeight - visibleHeight);
    const finalScrollTop = Math.max(0, Math.min(maxScrollTop, desiredScrollTop));
    container.scrollTo ? container.scrollTo({ top: finalScrollTop, behavior: "smooth" }) : (container.scrollTop = finalScrollTop);
  } catch (e) {
    try { container.scrollTop = Math.max(0, msgEl.offsetTop - 100); } catch {}
  }
}

function scrollToBottom() {
  const container = scrollRef.current;
  if (!container) return;
  try {
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  } catch {
    container.scrollTop = container.scrollHeight;
  }
}

function scrollAfterMedia(row, isUser) {
  if (!row) return;
  const imgs = Array.from(row.querySelectorAll("img"));
  if (imgs.length === 0) {
    if (isUser) scrollToBottom();
    else scrollAIMessageTopAboveComposer(row);
    return;
  }
  let loaded = 0;
  const done = () => {
    loaded++;
    if (loaded >= imgs.length) {
      if (isUser) scrollToBottom();
      else scrollAIMessageTopAboveComposer(row);
    }
  };
  imgs.forEach((img) => {
    if (img.complete) done();
    else {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
  });
}

useEffect(() => {
  const container = scrollRef.current;
  if (!container) return; 
  const curLen = messages.length;
  prevMessagesLenRef.current = curLen;
  if (curLen === 0) return;
  const rows = container.querySelectorAll(".aa-message-row");
  const lastRow = rows[rows.length - 1];
  if (!lastRow) {
    scrollToBottom();
    return;
  }
  const lastMsg = messages[curLen - 1];
  if (lastMsg && lastMsg.isUser) {
    requestAnimationFrame(() => { scrollAfterMedia(lastRow, true); });
    return;
  }
  const DELAY = 40;
  const t = setTimeout(() => { scrollAfterMedia(lastRow, false); }, DELAY);
  return () => clearTimeout(t);
}, [messages]);

useEffect(() => {
  return () => {
    try {
      previews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); });
    } catch {}
  };
}, [previews]);

function handleFileChange(e) {
  const chosen = Array.from(e.target.files || []);
  if (!chosen.length) return;

  const newFiles = chosen.map((f) => ({
    file: f,
    name: f.name,
    type: f.type,
    size: f.size,
    previewUrl: URL.createObjectURL(f) // UI only
  }));

  setFiles((prev) => [...prev, ...newFiles.map(f => f.file)]);

  setPreviews((prev) => [
    ...prev,
    ...newFiles.map(f => ({
      name: f.name,
      size: f.size,
      url: f.previewUrl
    }))
  ]);

  if (fileInputRef.current) fileInputRef.current.value = "";
}

function removePreview(name, size) {
  setFiles((f) => f.filter((x) => !(x.name === name && x.size === size)));
  setPreviews((p) => {
    const removed = p.find((x) => x.name === name && x.size === size);
    if (removed && removed.url) {
      try { URL.revokeObjectURL(removed.url); } catch (e) {}
    }
    return p.filter((x) => !(x.name === name && x.size === size));
  });
}

function handleWhatsAppShare(rawAnswerText) {
  if (!rawAnswerText) return;

  const cleanForShare = (str) => {
    if (!str) return "";

    let out = String(str);

    // 1. Remove generated image tags and HTML
    out = out
      .replace(/\[GeneratedImageURL:[^\]]*\]/g, "")
      .replace(/<Diagram:[^>]*>/gi, "")
      .replace(/Caption:\s*.*$/gim, "")
      .replace(/<\/?[^>]+(>|$)/g, "");

    // 2. Convert LaTeX Matrices to Text Grid format
    // e.g., \begin{pmatrix} a & b \\ c & d \end{pmatrix} -> [ a  b ]\n[ c  d ]
    out = out.replace(/\\begin\{p?matrix\}([\s\S]*?)\\end\{p?matrix\}/g, (match, inner) => {
      let rows = inner.split(/\\\\/).map(row => 
        "[" + row.split("&").map(cell => cell.trim()).join("   ") + "]"
      );
      return "\n" + rows.join("\n") + "\n";
    });

    // 3. Convert LaTeX Fractions to readable text e.g., (numerator)/(denominator)
    // Run multiple times for nested/multiple fractions
    for (let i = 0; i < 3; i++) {
      out = out.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)");
    }

    // 4. Convert Square Roots and Common Math Symbols
    out = out
      .replace(/\\sqrt\{([^}]*)\}/g, "√($1)")
      .replace(/\\pm/g, "±")
      .replace(/\\times/g, "×")
      .replace(/\\cdot/g, "·")
      .replace(/\\pi/g, "π")
      .replace(/\\theta/g, "θ")
      .replace(/\\alpha/g, "α")
      .replace(/\\beta/g, "β")
      .replace(/\\infty/g, "∞");

    // 5. Remove layout/styling wrappers
    out = out
      .replace(/\\(?:boxed|box)\{([^}]+)\}/g, "$1")
      .replace(/\\(left|right)/g, "")
      .replace(/\\(?:text|mathrm|mathbf|mathit)\{([^}]+)\}/g, "$1");

    // 6. Strip remaining LaTeX math block delimiters ($ or $$)
    out = out
      .replace(/\$\$(.*?)\$\$/gs, "$1")
      .replace(/\$(.*?)\$/gs, "$1");

    // 7. Clean Markdown Headers and Formatting for WhatsApp Compatibility
    out = out
      .replace(/^#{1,6}\s*/gm, "") // Remove markdown headings
      .replace(/\*\*(.*?)\*\*/g, "*$1*") // Convert Markdown bold (**text**) to WhatsApp bold (*text*)
      .replace(/\[\d+\s*marks?\]|\[\d+m\]|\(\d+\s*marks?\)/gi, (m) => `*${m}*`); // Bold marks badges

    // 8. Format Final Answer Header nicely for WhatsApp
    out = out.replace(
      /FINAL ANSWER\s*:?\s*(.+)$/gim,
      "\n🎯 *FINAL ANSWER*\n$1"
    );

    // Collapse excessive blank lines
    out = out.replace(/\n{3,}/g, "\n\n").trim();

    return out;
  };

  const cleanedAnswer = cleanForShare(rawAnswerText);
  if (!cleanedAnswer) return;

  const currentSession = sessions.find(
    (s) => s.id === currentSessionId
  );

  const rawQuestion = lastUserQuestionFull(currentSession);
  const cleanedQuestion = cleanForShare(rawQuestion);

  let shareText = "";

  if (cleanedQuestion) {
    shareText =
      "📘 *AGNI TUTOR*\n\n" +
      "❓ *Question*\n" +
      cleanedQuestion +
      "\n\n" +
      "✅ *Answer*\n" +
      cleanedAnswer +
      "\n\n" +
      "🤖 *Solved using AGNI Tutor*\n" +
      "🌐 https://agnitutor.com";
  } else {
    shareText =
      cleanedAnswer +
      "\n\n🤖 *AGNI Tutor*\n🌐 https://agnitutor.com";
  }

  const shareUrl =
    `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  window.open(
    shareUrl,
    "_blank",
    "noopener,noreferrer"
  );
}

/* ---------- SpeechRecognition ---------- */
function startListening() {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition || null;
  if (!SR) {
    alert("Voice not supported in this browser. Use Chrome or Edge.");
    return;
  }
  if (recognitionRef.current) {
    try { recognitionRef.current.stop(); } catch {}
    recognitionRef.current = null;
  }
  const rec = new SR();
  rec.lang = "en-IN";
  rec.interimResults = true;
  rec.continuous = false;
  rec.maxAlternatives = 1;
  let finalTranscript = "";
  rec.onstart = () => setIsListening(true);
  rec.onresult = (ev) => {
    let interim = "";
    for (let i = ev.resultIndex; i < ev.results.length; ++i) {
      const r = ev.results[i];
      if (r.isFinal) finalTranscript += r[0].transcript;
      else interim += r[0].transcript;
    }
    setInput(finalTranscript || interim);
  };
  rec.onerror = (ev) => {
    console.warn("STT error", ev);
    setIsListening(false);
    if (ev && ev.error && ev.error !== "no-speech") {
      alert("Voice error: " + (ev.error || "unknown"));
    }
  };
  rec.onend = () => setIsListening(false);
  recognitionRef.current = rec;
  try { rec.start(); } catch (e) { console.error("rec.start", e); setIsListening(false); }
}

function stopListening() {
  if (recognitionRef.current) {
    try { recognitionRef.current.stop(); } catch {}
    recognitionRef.current = null;
  }
  setIsListening(false);
}

const lastUserQuestionFull = (session) => {
  try {
    if (!session || !session.messages) return "";
    for (let i = session.messages.length - 1; i >= 0; i--) {
      const m = session.messages[i];
      if (m.isUser && m.text) {
        return m.text;
      }
    }
    return session.messages[0]?.text || "";
  } catch {
    return "";
  }
};

const lastUserQuestionSafe = (session) => {
  try {
    if (!session || !session.messages) return "";

    for (let i = session.messages.length - 1; i >= 0; i--) {
      const m = session.messages[i];

      if (m.isUser && m.text) {
        const text = m.text.trim();
        return text.length > 40 ? text.slice(0, 40) + "..." : text;
      }
    }

    const first = session.messages[0]?.text || "";
    return first.length > 40 ? first.slice(0, 40) + "..." : first;
  } catch {
    return "";
  }
};

function isFollowUp(text = "") {
  const t = text.toLowerCase().trim();

  return (
    /^(continue|go on|next)$/.test(t) ||
    /(explain more|explain step by step|elaborate further|continue explanation)/.test(t) ||
    /(same pattern|same format|same model|similar paper|same type)/.test(t)
  );
}

async function handleSend() {
  if (!input.trim() && files.length === 0) return;

  const storedFiles = await Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();

          reader.onload = () =>
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              url: reader.result,
            });

          reader.onerror = reject;

          reader.readAsDataURL(file);
        })
    )
  );

  const rawText = input.trim();
  const displayText = removeAutoSymbols(rawText);

  const userMsg = {
    id: `u-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    isUser: true,
    text: displayText,
    files: storedFiles,
    ts: Date.now(),
  };

  appendToSession(userMsg);

  setInput("");
  setPreviews([]);
  setIsLoading(true);

  try {
    let aiText = null;

    // Calculator shortcut
    if (/^[0-9\s()+\-*/%.]+$/.test(rawText)) {
      try {
        const result = Function(
          `"use strict"; return (${rawText})`
        )();

        aiText = `### 🎯 FINAL ANSWER

**${result}**`;
      } catch {
        aiText = null;
      }
    }

    let finalPrompt = rawText;

    // Follow-up detection
    if (isFollowUp(rawText)) {
      const lastQuestion = [...messages]
        .reverse()
        .find((m) => m.isUser && m.text !== rawText);

      if (lastQuestion) {
        finalPrompt = `
Previous Question:
${lastQuestion.text}

User says:
"${rawText}"

Explain the above question step by step with full working.
`.trim();
      }
    }

    // Gemini / Backend Response
    if (!aiText) {
      aiText = await getAcademicAnswer(finalPrompt, files);
    }

    // ==========================
    // AI RESPONSE CLEANUP
    // ==========================

    if (aiText) {
      aiText = String(aiText)

        // Remove boxed answers
        .replace(/\\boxed\s*\{([^}]*)\}/g, "$1")

        // Remove \left \right
        .replace(/\\left/g, "")
        .replace(/\\right/g, "")

        // Convert text blocks
        .replace(
          /\\(?:text|mathrm|mathbf|mathit)\{([^}]*)\}/g,
          "$1"
        )

        // Clean extra spaces
        .replace(/[ \t]+\n/g, "\n")

        .trim();
    }

    //console.log("FINAL AI RESPONSE:", aiText);

    const aiMsg = {
      id: `ai-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      isUser: false,
      text: aiText,
      ts: Date.now(),
      prompt: rawText,
    };

    appendToSession(aiMsg);

  } catch (error) {
    console.error("handleSend error:", error);

    appendToSession({
      id: `err-${Date.now()}`,
      isUser: false,
      text:
        "Sorry, something went wrong. Please try again.",
      ts: Date.now(),
    });

  } finally {
    setIsLoading(false);
    setFiles([]);
  }
}

function onKeyDown(e) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    handleSend();
  }
}

function ImageZoomWithChat({ thumb, full, chatHtml }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onWheel = (e) => {
    if (!open) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0016;
    setScale((s) => Math.min(4, Math.max(0.6, +(s + delta).toFixed(3))));
  };

  const startDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current.dragging = true;
    dragRef.current.sx = clientX;
    dragRef.current.sy = clientY;
    dragRef.current.ox = pos.x;
    dragRef.current.oy = pos.y;
    document.body.style.cursor = "grabbing";
    window.addEventListener("pointermove", moveDrag, { passive: false });
    window.addEventListener("pointerup", endDrag, { passive: true });
    window.addEventListener("touchmove", moveDrag, { passive: false });
    window.addEventListener("touchend", endDrag, { passive: true });
  };

  const moveDrag = (e) => {
    if (!dragRef.current.dragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const dx = clientX - dragRef.current.sx;
    const dy = clientY - dragRef.current.sy;
    setPos({ x: dragRef.current.ox + dx, y: dragRef.current.oy + dy });
    e.preventDefault?.();
  };

  const endDrag = () => {
    dragRef.current.dragging = false;
    document.body.style.cursor = "auto";
    try {
      window.removeEventListener("pointermove", moveDrag);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("touchmove", moveDrag);
      window.removeEventListener("touchend", endDrag);
    } catch {}
  };

  useEffect(() => {
    if (!open) {
      setScale(1);
      setPos({ x: 0, y: 0 });
      dragRef.current = { dragging: false, sx: 0, sy: 0, ox: 0, oy: 0 };
    }
  }, [open]);

  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  const css = `
    .zz-thumb-wrapper { position: relative; display:inline-block; cursor: zoom-in; }
    .zz-thumb-img { width: 80px; height: 80px; object-fit: cover; border-radius:6px; }
    .zz-thumb-chat { position: absolute; left:8px; bottom:8px; background: rgba(255,255,255,0.95); padding:6px 8px; border-radius:8px; font-size:12px; box-shadow: 0 6px 18px rgba(0,0,0,0.06); max-width:66%; }
    .zz-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 14000; display:flex; align-items:center; justify-content:center; }
    .zz-controls { position: fixed; top: 18px; right: 18px; display:flex; gap:8px; z-index: 14010; }
    .zz-controls button { padding:6px 10px; border-radius:8px; cursor:pointer; }
    .zz-container { max-width: 90%; max-height: 86%; overflow: hidden; background: #fff; padding: 12px; border-radius: 12px; position: relative; display:flex; align-items:center; justify-content:center; }
    .zz-image { max-width: 100%; max-height: 100%; user-select: none; transition: transform 0.04s linear; will-change: transform; cursor: grab; }
    .zz-in-chat { position: absolute; z-index: 14005; background: rgba(255,255,255,0.98); padding: 10px 12px; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.15); max-width: 360px; font-size: 14px; cursor: grab; user-select: none; }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="zz-thumb-wrapper" onClick={() => setOpen(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") setOpen(true); }}>
        <img src={sanitizeImageUrl(thumb)} alt="attachment-thumb" className="zz-thumb-img" draggable={false} />
        {chatHtml ? <div className="zz-thumb-chat">{typeof chatHtml === "string" ? <div dangerouslySetInnerHTML={{ __html: chatHtml }} /> : chatHtml}</div> : null}
      </div>

      {open && (
        <div
          className="zz-overlay"
          onMouseMove={moveDrag}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchMove={moveDrag}
          onTouchEnd={endDrag}
          role="dialog"
          aria-modal="true"
        >
          <div className="zz-controls">
            <button onClick={() => setScale((s) => Math.min(4, +(s + 0.25).toFixed(2)))}>＋</button>
            <button onClick={() => setScale((s) => Math.max(0.6, +(s - 0.25).toFixed(2)))}>－</button>
            <button onClick={reset}>Reset</button>
            <button onClick={() => setOpen(false)}>Close ✕</button>
          </div>

          <div className="zz-container" onWheel={(e) => { onWheel(e); }} onMouseDown={startDrag} role="presentation" onTouchStart={startDrag}>
            <img
              src={sanitizeImageUrl(full)}
              alt="zoomed"
              className="zz-image"
              draggable={false}
              style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
            />

            {chatHtml && (
              <div
                className="zz-in-chat"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const box = e.currentTarget;
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const rect = box.getBoundingClientRect();
                  const origLeft = rect.left;
                  const origTop = rect.top;

                  function onMove(me) {
                    try {
                      box.style.left = `${origLeft + (me.clientX - startX)}px`;
                      box.style.top = `${origTop + (me.clientY - startY)}px`;
                    } catch {}
                  }
                  function onUp() { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); }
                  window.addEventListener("mousemove", onMove);
                  window.addEventListener("mouseup", onUp);
                }}
                style={{ right: 28, bottom: 28 }}
              >
                {typeof chatHtml === "string" ? <div dangerouslySetInnerHTML={{ __html: chatHtml }} /> : chatHtml}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const TEXTAREA_MAX = 200; // px
const resizeTextarea = (el) => {
  if (!el) return;
  try {
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, TEXTAREA_MAX);
    el.style.height = h + "px";
  } catch {}
};

function AnimatedDots({ size = 8, gap = 6 }) {
  const inline = `
    .aa-animated-dots { display:inline-flex; align-items:center; gap:${gap}px; margin-left:8px; }
    .aa-animated-dots .dot {
      width:${size}px; height:${size}px; border-radius:50%; background:currentColor; opacity:0.18;
      transform: translateY(0);
      animation: aa-dot-bounce 900ms infinite ease-in-out;
    }
    .aa-animated-dots .dot:nth-child(1) { animation-delay: 0ms; }
    .aa-animated-dots .dot:nth-child(2) { animation-delay: 150ms; }
    .aa-animated-dots .dot:nth-child(3) { animation-delay: 300ms; }
    @keyframes aa-dot-bounce {
      0% { opacity:0.18; transform: translateY(0); }
      40% { opacity:1; transform: translateY(-6px); }
      80% { opacity:0.3; transform: translateY(0); }
      100% { opacity:0.18; transform: translateY(0); }
    }
    .aa-thinking-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-top:0px;   
      padding-bottom: 400px;   
      color: #000; 
    }
  `;
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: inline }} />
      <span className="aa-animated-dots" aria-hidden>
        <span className="dot" />
        <span className="dot" />
        <span className="dot" />
      </span>
    </>
  );
}

const splashInlineCSS = `
  .aa-splash { position: fixed; inset: 0; display:flex; align-items:center; justify-content:center; flex-direction:column; background: linear-gradient(180deg, #0f172a 0%, #020617 100%); color: #f8fafc; z-index: 99999; font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial; }
  .aa-splash-brand { font-size: 28px; letter-spacing: 1px; margin-bottom: 8px; }
  .aa-splash-sub { font-size: 14px; opacity: 0.9; }
`;

if (showSplash) {
  return (
    <div>
      <style dangerouslySetInnerHTML={{ __html: splashInlineCSS }} />
      <div className="aa-splash" role="status" aria-live="polite">
        <div className="aa-splash-brand">AGNI TUTOR</div>
        <div className="aa-splash-sub">Loading…</div>
      </div>
    </div>
  );
}

return (
  <div className="aa-full" role="application" aria-label="Academic AI Application">
    <div className="aa-card" ref={aaCardRef} role="main">

        <SidebarHistoryPanel 
        currentUser={currentUser}
        newSessions={newSessions}
        openDeleteAllModal={openDeleteAllModal}
        showDeleteAllModal={showDeleteAllModal}
        DangerModal={DangerModal}
        confirmDeleteAll={confirmDeleteAll}
        closeDeleteAllModal={closeDeleteAllModal}
        sessions={sessions}
        groupSessionsByDate={groupSessionsByDate}
        currentSessionId={currentSessionId}
        openSession={openSession}
        confirmingDelete={confirmingDelete}
        setConfirmingDelete={setConfirmingDelete}
        getSubjectIcon={getSubjectIcon}
        lastUserQuestionSafe={lastUserQuestionSafe}
        leftFooterStyle={leftFooterStyle}
      />

      <main className="aa-main" role="region" aria-label="Chat area">
        
        <AppHeader 
        setShowHistoryMobile={setShowHistoryMobile}
        showFeedback={showFeedback}
        feedbackText={feedbackText}
        setFeedbackText={setFeedbackText}
        feedbackType={feedbackType}
        setFeedbackType={setFeedbackType}
        setShowFeedback={setShowFeedback}
        handleSubmitFeedback={handleSubmitFeedback}
        showFeedbackSuccess={showFeedbackSuccess}
      />

        <div className="answer-wrapper" style={{ position: "relative" }}>

  {uiMode === "welcome" ? (
    <WelcomePanel onStart={startChatWithMode} />
  ) : (
        <div className="answer-scroll" ref={scrollRef} aria-live="polite">

          {messages.map((m) => (
            <div key={m.id} className="aa-message-row" data-msgid={m.id}>

              {m.isUser ? (
                <div className="user-bubble" aria-live="polite">
                  <div className="user-bubble-inner">

                    <div className="user-msg-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="Copy this question"
                        aria-label="Copy this question"
                        onClick={async (e) => {
                          e.stopPropagation();
                          const txt = m.text || "";
                          if (!txt) return;
                          await handleCopyText(txt, setShowCopyToast);
                        }}
                      >
                        <span className="material-icons" style={{ fontSize: "16px" }}>
                          content_copy
                        </span>
                      </button>

                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit and regenerate"
                        aria-label="Edit and regenerate"
                        onClick={(e) => {
                          e.stopPropagation();
                          const txt = m.text || "";
                          if (!txt) return;

                          setInput(txt);

                          try {
                            if (composerAnchorRef.current) {
                              composerAnchorRef.current.scrollIntoView({
                                behavior: "smooth",
                                block: "end",
                              });
                            }
                          } catch (err) {
                            console.warn("scroll to composer failed", err);
                          }
                        }}
                      >
                        <span className="material-icons" style={{ fontSize: "16px" }}>
                          edit
                        </span>
                      </button>

                    </div>

                    <div className="user-text-wrapper">
                        <div className="user-text">
                          {(() => {
                            const text = (m.text || "").trim();

                            return (
                              <div className="markdown-math-renderer">
                                <ReactMarkdown 
                                >
                                  {text}
                                </ReactMarkdown>
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                    {m.files && m.files.length > 0 && (
                      <div className="attachments">
                        {m.files.map((f, idx) =>
                          f.type && f.type.includes("pdf") ? (
                            <div key={idx} className="pdf-chip">
                              <small>{f.name}</small>
                            </div>
                          ) : (
                            <ImageZoomWithChat
                              key={idx}
                              thumb={f.url}
                              full={f.url}
                              chatHtml={null}
                            />
                          )
                        )}
                      </div>
                    )}

                  </div>
                </div>
              ) : (
                <div className="ai-bubble">

  {m.text ? (
    renderFormattedText(m.text)
  ) : (
    <div className="muted">No response generated.</div>
  )}

        <div className="ai-message-actions-wrap">
            <div className="message-actions">
              <button
                className={`action-btn like-btn ${
                  selectedVote[m.id] === "like" ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedVote(prev => ({
                    ...prev,
                    [m.id]: "like"
                  }))
                }
              >
                <span className="material-icons">thumb_up</span>
              </button>

              <button
                className={`action-btn dislike-btn ${
                  selectedVote[m.id] === "dislike" ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedVote(prev => ({
                    ...prev,
                    [m.id]: "dislike"
                  }))
                }
              >
                <span className="material-icons">thumb_down</span>
              </button>

              <button
                className="action-btn whatsapp"
                onClick={() => handleWhatsAppShare(m.text)}
                title="WhatsApp"
              >
                <i className="fab fa-whatsapp"></i>
              </button>

              {/* <button
                className="action-btn x-btn"
                onClick={() =>
                  window.open(
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(m.text || "")}`,
                    "_blank"
                  )
                }
                title="Share on X"
              >
                <i className="fab fa-x-twitter"></i>
              </button> */}

              <button
                className="action-btn"
                title="Copy"
                onClick={async () => {
                  const ok = await handleCopyText(
                    m.text || "",
                    setShowCopyToast
                  );

                  if (ok) {
                    setCopiedMsgId(m.id);
                    setTimeout(() => setCopiedMsgId(null), 1200);
                  }
                }}
              >
                <span className="material-icons">
                  {copiedMsgId === m.id ? "check" : "content_copy"}
                </span>
              </button>
            </div>

          </div>
        
      </div>

              )}

            </div>
          ))}

          {isLoading && (
            <div
              className="aa-thinking-row aithinking"
              role="status"
              aria-live="polite"
            >
              <span>Agni Tutor is thinking</span>
              <AnimatedDots />
            </div>
          )}

        </div>
      )}

    </div>

        <div className="composer-wrapper-premium" ref={composerAnchorRef}>
          <div className="composer-panel-premium">
            
            <div className={`input-pill-container ${previews.length > 0 ? 'has-attachment' : ''}`}>
              
              {previews.length > 0 && (
                <div className="single-preview-box">
                  <img 
                    src={sanitizeImageUrl(previews[0].url, previews[0].name)} 
                    alt="Upload preview" 
                  />
                  <button 
                    className="remove-attachment-btn" 
                    onClick={() => removePreview(previews[0].name, previews[0].size)}
                    title="Remove image"
                  >
                    <span className="material-icons">cancel</span>
                  </button>
                </div>
              )}

              <input
                id="composer-file-input"
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                multiple={false}
              />
              
              {previews.length === 0 && (
                <label htmlFor="composer-file-input" className="pill-icon-btn camera-btn">
                  <span className="material-icons">photo_camera</span>
                </label>
              )}

              <button 
                onClick={() => (isListening ? stopListening() : startListening())}
                className={`pill-icon-btn mic-btn ${isListening ? 'active' : ''}`}
              >
                <span className="material-icons">{isListening ? "mic_off" : "mic"}</span>
              </button>

              <textarea
                ref={(el) => el && resizeTextarea(el)}
                className="premium-textarea"
                placeholder="Ask your question..."
                value={input}
                rows="1"
                onChange={(e) => setInput(e.target.value)}
                onInput={(e) => {
                  if (window.innerWidth > 480) {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }
                }}
                onKeyDown={onKeyDown}
              />

              <button 
                onClick={handleSend} 
                disabled={!input.trim() && previews.length === 0}
                className="premium-send-btn"
              >
                <span className="material-icons">send</span>
              </button>
            </div>
            <div className="muted small composer-note">Agni Tutor can make mistakes. Check important info.</div>
          </div>
        </div>

      </main>
    </div>

    {/* <DraggableScrollButton 
      scrollToBottom={scrollToBottom} 
      showScrollDown={true}
    /> */}

   <MobileHistoryPanel
      showHistoryMobile={showHistoryMobile}
      sessions={sessions}
      currentSessionId={currentSessionId}
      confirmingDelete={confirmingDelete}
      setConfirmingDelete={setConfirmingDelete}
      setShowHistoryMobile={setShowHistoryMobile}
      openSession={openSession}
      deleteSession={deleteSession}
      groupSessionsByDate={groupSessionsByDate}
      lastUserQuestionSafe={lastUserQuestionSafe}
      getSubjectIcon={getSubjectIcon}
      showSocialMenu={showSocialMenu}
      setShowSocialMenu={setShowSocialMenu}
      socialMenuRef={socialMenuRef}
      setShowFeedback={setShowFeedback}
      newSessions={newSessions}  
      openDeleteAllModal={openDeleteAllModal}  
      confirmDeleteAll={confirmDeleteAll}      
      closeDeleteAllModal={closeDeleteAllModal} 
      DangerModal={DangerModal}
      currentUser={currentUser}
    />

    <CopyToast visible={showCopyToast} />
  </div>
);
}